import { SearchX } from 'lucide-react';
import type { MessageKey } from '@featit/shared/i18n/messages/index';
import { Waiting } from '@/components/Waiting';
import { useT } from '@/i18n/LocaleProvider';

/**
 * What a search box is described by while the server refuses its text (I-1319): the note's id, so
 * the box can be `aria-invalid` and point at the sentence that says what to correct.
 */
export const SEARCH_CORRECTION_ID = 'search-correction';

/**
 * A search the server refused for good, as a note to correct (I-1319,
 * `docs/concept-generic-view-engine.md`, *a search the reader corrects*): the server's sentence,
 * once, under the row that holds the box. Not a failure of the list, which keeps what it showed: no
 * reference, nothing to try again.
 */
export function SearchCorrection({ message }: { message: string }) {
  return (
    <p
      id={SEARCH_CORRECTION_ID}
      role="status"
      data-search-correction=""
      className="mb-3 flex shrink-0 items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-[13px] text-amber-900 [overflow-wrap:anywhere] dark:bg-amber-950/40 dark:text-amber-100"
    >
      <SearchX className="mt-px h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">{message}</span>
    </p>
  );
}

/** What the list under a refused search shows instead of its answer (I-1370): the key. */
export const SEARCH_NOT_RUN = {
  /** The Items list with no search answered before: every item, unfiltered. */
  all: 'navigation.search.notRun.all',
  /** The Items list holding the answer to the search before this one. */
  last: 'navigation.search.notRun.last',
  /** A view's held rows: whatever its query before this one answered. */
  before: 'navigation.search.notRun.before',
} as const satisfies Record<string, MessageKey>;

export type SearchNotRunType = keyof typeof SEARCH_NOT_RUN;

/**
 * The list's own line under a refused search (I-1370, `docs/concept-generic-view-engine.md`, *a
 * search the reader corrects*): the list keeps what it showed, and says so, so its rows and its
 * count never read as the refused search's result. Drawn directly above the rows, apart from the
 * correction note, which is the server's sentence alone.
 */
export function SearchNotRun({ shows }: { shows: SearchNotRunType }) {
  const t = useT();
  return (
    <p
      data-search-not-run={shows}
      className="mb-2 shrink-0 px-1 text-[13px] text-gray-600 [overflow-wrap:anywhere] dark:text-gray-300"
    >
      {t(SEARCH_NOT_RUN[shows])}
    </p>
  );
}

/**
 * A search the list has not had the answer to yet (I-1317): said where the rows are, from the
 * keystroke until the answer to the text in the box. Never "No matches", which is about an answer.
 */
export function SearchingStatus({ className = '' }: { className?: string }) {
  const t = useT();
  return (
    <div
      role="status"
      aria-label={t('navigation.search.searching')}
      data-search-pending=""
      className={`flex items-center gap-2 px-3 py-2 text-[13px] text-gray-500 dark:text-gray-400 ${className}`}
    >
      <Waiting size="inline" />
      <span>{t('navigation.search.searching')}</span>
    </div>
  );
}
